<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import {
    VfListCompose,
    VfListRow,
    computeThClass,
    sortListByField,
    useHttpClient,
    useInlineTable,
    useModuleAction,
    type InlineTable,
    type ListFieldConfiguration,
    type ListState,
    type ModuleConfiguration,
    type NormalizedListFieldGroupConfiguration,
} from "@/vf"

const props = defineProps<{
    config: ModuleConfiguration
    listState: ListState
    fields: ListFieldConfiguration[]
    filteredFieldGroups: NormalizedListFieldGroupConfiguration[]
    inlineTable?: InlineTable
}>()

const moduleAction = useModuleAction()
const inlineTable = props.inlineTable || useInlineTable(false)

const deleteConfirm = createInlineConfirmCollection<{ item: any }>((item: any) => ({
    action() {
        useHttpClient()
            .delete(props.config.entityPath(item))
            .then(() => inlineTable.emit("refresh"))
    },
}))
</script>

<template>
    <table class="table vf-crud-list" :class="{ 'table-row-tabs': config.list.showOpenRowAsTab }">
        <thead>
            <VfListCompose
                :config="config"
                :inline-table="inlineTable"
                :colspan="fields.length + (config.list.buttonColumn() || $slots.listButtons ? 1 : 0)"
                row-id="new"
            ></VfListCompose>
            <slot name="additionalHeaderRow"></slot>
            <template v-if="config.list.showFieldGroupsHeadRow">
                <tr class="vf-field-group-header">
                    <template v-for="(fieldGroup, groupIndex) of filteredFieldGroups" :key="fieldGroup.name">
                        <th
                            :colspan="fieldGroup.fields.length"
                            class="vf-first-in-group vf-last-in-group"
                            :class="{ 'vf-first-in-table': groupIndex === 0 }"
                        >
                            {{ $t(fieldGroup.title) }}
                        </th>
                    </template>
                </tr>
            </template>
            <tr>
                <template v-for="(fieldGroup, groupIndex) of filteredFieldGroups">
                    <template v-for="(field, fieldIndex) of fieldGroup.fields" :key="field.name">
                        <th
                            :class="computeThClass(field, fieldGroup, fieldIndex, groupIndex, listState).value"
                            @click="sortListByField(listState, field)"
                        >
                            <template v-if="typeof field.title === 'string'">
                                {{ $t(field.title) }}
                            </template>
                            <template v-if="typeof field.title === 'function'">
                                <component :is="field.title({ listState })"></component>
                            </template>

                            <div class="float-right d-grid">
                                <Transition
                                    enter-active-class="transition"
                                    enter-from-class="scale-0"
                                    enter-to-class="scale-1"
                                    leave-active-class="transition"
                                    leave-from-class="scale-1"
                                    leave-to-class="scale-0"
                                >
                                    <i
                                        class="grid-one fa fa-sort-up"
                                        v-if="listState.orderField === field.name && listState.orderDirection === 'asc'"
                                    ></i>
                                    <i
                                        class="grid-one fa fa-sort-down"
                                        v-else-if="
                                            listState.orderField === field.name && listState.orderDirection === 'desc'
                                        "
                                    ></i>
                                </Transition>
                            </div>
                        </th>
                    </template>
                </template>
                <th v-if="config.list.buttonColumn() || $slots.listButtons" class="border-left-0 text-right">
                    {{ $t("crud:list.actions") }}
                </th>
            </tr>

            <slot name="additionalHeaderRowAfter" v-bind="{ list: listState.list }"></slot>
        </thead>
        <tbody>
            <template v-for="item in listState.list" :key="item.id">
                <VfListRow v-bind="{ config, listState, item, moduleAction, filteredFieldGroups }">
                    <template #listButtons v-if="config.list.buttonColumn()">
                        <button
                            v-if="config.list.showButton(item)"
                            :title="$t('crud:list.show')"
                            class="btn btn-secondary"
                            type="button"
                            @click="listState.columnClicked(item, 'button:show')"
                        >
                            <i class="fa fa-fw fa-eye"></i>
                        </button>
                        <button
                            v-if="config.list.editButton(item)"
                            :title="$t('crud:list.edit')"
                            type="button"
                            class="btn btn-secondary"
                            @click="listState.editEntity(item)"
                        >
                            <i class="fa fa-fw fa-pencil"></i>
                        </button>
                        <VarsityInlineConfirmButton
                            :controller="deleteConfirm(item)"
                            :title="$t('crud:list.delete')"
                            class="btn btn-danger"
                            v-if="config.list.deleteButton(item)"
                        >
                            <i class="fa fa-fw fa-trash"></i>
                        </VarsityInlineConfirmButton>
                    </template>
                    <!-- slot passthrough-->
                    <template v-for="name in Object.keys($slots)" v-slot:[name]="slotData">
                        <slot :name="name" v-bind="slotData" />
                    </template>
                </VfListRow>
                <VfListCompose
                    :config="config"
                    :inline-table="inlineTable"
                    :colspan="fields.length + (config.list.buttonColumn() || $slots.listButtons ? 1 : 0)"
                    :row-id="item.id"
                ></VfListCompose>
                <tr v-if="config.list.buttonColumn()" class="confirmations">
                    <td :colspan="fields.length + (config.list.buttonColumn() || $slots.listButtons ? 1 : 0)">
                        <VarsityInlineConfirm :controller="deleteConfirm(item)" v-if="config.list.deleteButton(item)">
                            <template #confirmation>
                                {{
                                    $t("crud:delete.confirmation_detail", {
                                        type: $t(config.readableName.singular),
                                        name: config.readableName.entity(item),
                                    })
                                }}
                            </template>
                            <template #success>
                                {{
                                    $t("crud:delete.success.message", {
                                        type: $t(config.readableName.singular),
                                        name: config.readableName.entity(item),
                                        interpolation: { escapeValue: false },
                                    })
                                }}
                            </template>
                        </VarsityInlineConfirm>
                        <slot name="confirmations" v-bind="{ item, listState }"></slot>
                    </td>
                </tr>
            </template>
        </tbody>
    </table>
</template>
